import {useState, useEffect, useRef} from "react";
import {Dropdown} from "primereact/dropdown";
import {InputNumber} from "primereact/inputnumber";
import {AutoComplete} from "primereact/autocomplete";
import {SpinnerIcon} from "primereact/icons/spinner";
import MaterialFields from "./MaterialFields";
import {toast} from "react-toastify";
import {useDemandFurnitures, useSearchDemands} from "../../../hooks/useDemand";
import {usePutUnique} from "../../../hooks/useUnique";
import {useCreateVipuskForDemand} from "../../../hooks/useVipusk";

const ClientTab = ({onVipuskCreated}) => {
	const autoCompleteRef = useRef();
	const prevIsLoadingRef = useRef(false);
	const [selectedDemand, setSelectedDemand] = useState();
	const [baseQuery, setBaseQuery] = useState("");
	const [inputValue, setInputValue] = useState("");
	const [clientFurniture, setClientFurniture] = useState([]);
	const [selectedFurniture, setSelectedFurniture] = useState();
	const [furnitureAmount, setFurnitureAmount] = useState(0);
	const [packageQuantity, setPackageQuantity] = useState(0);
	const [mirror, setMirror] = useState(0);
	const [mirrorWood, setMirrorWood] = useState(0);
	const [lxdf, setLxdf] = useState(0);
	const [demandId, setDemandId] = useState(null);

	// api hooks
	const {
		data: allDemands,
		isFetched,
		isLoading: isLoadingDemand,
	} = useSearchDemands(baseQuery.length >= 3 ? baseQuery : "");

	const {data: demandFurnitures, isLoading: isLoadingFurniture} =
		useDemandFurnitures(demandId);

	const {mutateAsync: putUnique} = usePutUnique();
	const {mutateAsync: createVipuskForDemand} = useCreateVipuskForDemand();

	const filteredDemands = allDemands?.filter((d) =>
		d.doc_no.toLowerCase().includes(inputValue.toLowerCase()),
	);

	const searchDemands = (event) => {
		const q = event.query;
		setInputValue(q);
		if (!q.startsWith(baseQuery) || baseQuery.length < 3) {
			setBaseQuery(q);
		}
	};

	useEffect(() => {
		if (isFetched && baseQuery.length >= 3 && allDemands?.length === 0) {
			toast.warning(`Бундай рақамли заказ топилмади`);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isFetched, allDemands]);

	useEffect(() => {
		if (prevIsLoadingRef.current && !isLoadingDemand) {
			autoCompleteRef.current?.show();
		}
		prevIsLoadingRef.current = isLoadingDemand;
	}, [isLoadingDemand]);

	const formatDemandFurnitures = (demandFurnitures) => {
		return demandFurnitures.map((df) => {
			return {
				...df.furniture,
				name: `${df.category?.name || ""} ─ ${df.komplekt?.name || ""} ─ ${df.furniture?.name || ""}`,
				uniqueNum: df.blank_no,
				amount: df.amount,
				unique_id: df.unique.id,
				demand_furniture_id: df.id,
			};
		});
	};

	useEffect(() => {
		if (!demandFurnitures) return;
		if (demandFurnitures.length === 0) {
			toast.warn(`Seriya raqam yaratilib bo'lingan`);
			return;
		}
		setClientFurniture(formatDemandFurnitures(demandFurnitures));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [demandFurnitures]);

	useEffect(() => {
		setClientFurniture([]);
	}, [selectedDemand]);

	useEffect(() => {
		if (furnitureAmount > selectedFurniture?.amount)
			toast.warning(`Сиз мавжуд бўлган миқдордан кўп киритдингиз`);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [furnitureAmount]);

	const resetMaterials = () => {
		setPackageQuantity(0);
		setMirror(0);
		setMirrorWood(0);
		setLxdf(0);
	};

	const materialFields = [
		{
			label: "Упаковка сони",
			value: packageQuantity,
			onChange: setPackageQuantity,
		},
		{label: "Ойна", value: mirror, onChange: setMirror},
		{label: "Ойна учун таянч", value: mirrorWood, onChange: setMirrorWood},
		{label: "Lxdf", value: lxdf, onChange: setLxdf},
	];

	const renderFurnitureOption = (option) => (
		<div className="flex items-center justify-between !w-full">
			<span>{option.name}</span>
			<span className="ml-4 font-semibold">#{option.uniqueNum}</span>
		</div>
	);

	const generateClientSerial = async () => {
		const usedAmount = Math.min(selectedFurniture.amount, furnitureAmount);

		try {
			await Promise.all([
				putUnique({
					packageQuantity: packageQuantity,
					uniqueId: selectedFurniture.unique_id,
					glass_quantity: mirror,
					glass_wood_quantity: mirrorWood,
					lxdf_quantity: lxdf,
				}),
				createVipuskForDemand({
					furnitureId: selectedFurniture.id,
					uniqueId: selectedFurniture.unique_id,
					amount: usedAmount,
					demandFurnitureId: selectedFurniture.demand_furniture_id,
					package_quantity: packageQuantity,
					glass_quantity: mirror,
					glass_wood_quantity: mirrorWood,
					lxdf_quantity: lxdf,
				}).then((data) => onVipuskCreated(data.data)),
			]);

			const newAmount = selectedFurniture.amount - usedAmount;

			if (newAmount <= 0) {
				setClientFurniture((prev) =>
					prev.filter((item) => item.id !== selectedFurniture.id),
				);
				setSelectedFurniture(null);
				setFurnitureAmount(0);
			} else {
				const updatedFurniture = {...selectedFurniture, amount: newAmount};
				setClientFurniture((prev) =>
					prev.map((item) =>
						item.id === selectedFurniture.id ? updatedFurniture : item,
					),
				);
				setSelectedFurniture(updatedFurniture);
				setFurnitureAmount(newAmount);
			}

			resetMaterials();
		} catch (error) {
			console.log("Error:", error);
			toast.error("Ошибка при создании серийного номера!");
		}
	};

	return (
		<div className="w-full">
			<div className="flex items-center justify-between w-full gap-4 max-sm:flex-wrap max-sm:gap-0">
				<div className="relative flex flex-col w-full mb-8">
					<label className="text-sm mb-1.5 font-semibold">Заказ номери</label>
					<AutoComplete
						ref={autoCompleteRef}
						value={selectedDemand}
						field="doc_no"
						suggestions={filteredDemands}
						completeMethod={searchDemands}
						virtualScrollerOptions={{itemSize: 38}}
						onChange={(e) => setSelectedDemand(e.value)}
						onSelect={(e) => setDemandId(e.value.id)}
						loadingIcon={<></>}
						className="w-full rounded-lg"
						inputClassName="w-full border border-gray-300 p-2 rounded-lg"
					/>
					{isLoadingDemand && (
						<span className="absolute pointer-events-none right-3 bottom-3">
							<SpinnerIcon spin />
						</span>
					)}
				</div>

				<div className="flex flex-col w-full mb-8">
					<label className="text-sm mb-1.5 font-semibold">Мебель</label>
					<Dropdown
						value={selectedFurniture}
						onChange={(e) => {
							setFurnitureAmount(e.value.amount);
							setSelectedFurniture(e.value);
						}}
						options={clientFurniture}
						disabled={!clientFurniture.length}
						optionLabel="name"
						placeholder="Мебель танланг"
						className="w-full border border-gray-300 rounded-lg"
						loading={isLoadingFurniture}
						filter
						itemTemplate={renderFurnitureOption}
						valueTemplate={(option, props) =>
							option ?
								renderFurnitureOption(option)
							:	<span>{props.placeholder}</span>
						}
					/>
				</div>
			</div>

			<div className="flex flex-col mb-8">
				<label className="text-sm mb-1.5 font-semibold">Сони</label>
				<div className="flex items-end">
					<InputNumber
						value={selectedFurniture?.amount ?? furnitureAmount}
						onChange={(e) => setFurnitureAmount(e.value)}
						className="mr-2 !border-none w-fit"
						inputClassName="border p-2 rounded-lg border-gray-300"
						style={{
							border: "1px solid #ced4da",
							borderRadius: "0.5rem",
						}}
					/>
					<p className="">дона</p>
				</div>
			</div>

			<MaterialFields
				fields={materialFields}
				containerClassName="flex flex-wrap gap-4 mb-8 md:flex-nowrap"
				itemClassName="w-full md:w-1/4"
			/>

			<button
				className="w-full px-4 py-3 text-white rounded-md bg-blue"
				onClick={generateClientSerial}
				disabled={
					!selectedFurniture ||
					!packageQuantity ||
					selectedFurniture?.amount < furnitureAmount
				}>
				Серия рақам яратиш
			</button>
		</div>
	);
};

export default ClientTab;
